import { Col, Container, Dropdown, Form, Row } from 'react-bootstrap'
import classNames from 'classnames'
import React, { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { type FieldPath, type FieldValues, type UseFormRegister } from 'react-hook-form'
import { useDeepCompareEffect, useLatest } from 'react-use'
import { useDebounce } from 'use-debounce'
import { useOutsideClick } from '../../common/useOutsideClick'

import styles from './MultiSelectDropdown.module.scss'
import { Spinner } from '../Spinner/Spinner'

export interface MultiSelectOption {
  id: string
  label: string
  disabled?: boolean
}

const sameSelection = (a: string[], b: string[]): boolean => a.length === b.length && a.every((id, index) => id === b[index])

export interface MultiSelectDropdownProps<TFieldValues extends FieldValues = FieldValues> {
  register?: UseFormRegister<TFieldValues>
  name: FieldPath<NoInfer<TFieldValues>>
  options: MultiSelectOption[]
  onSelectedChanged?: (ids: string[]) => void
  initialSelectedOptions?: string[]
  placeholder?: string
  isInvalid?: boolean
  disabled?: boolean
  className?: string
  containerClassName?: string
  changeOnClose?: boolean
  TitleComponent?: ReactNode
  showSelectAllOption?: boolean
  showSelectedLabels?: boolean
  creatable?: boolean
  clearable?: boolean
  onSearchInputChange?: (searchInput: string) => void
  loading?: boolean
}

export const MultiSelectDropdown = <TFieldValues extends FieldValues = FieldValues>({
  register,
  name,
  options,
  onSelectedChanged,
  initialSelectedOptions = [],
  placeholder = 'Select Options',
  isInvalid = false,
  disabled = false,
  className = '',
  containerClassName,
  changeOnClose = true,
  TitleComponent,
  showSelectAllOption = false,
  showSelectedLabels = false,
  creatable = false,
  clearable = false,
  onSearchInputChange,
  loading = false,
}: MultiSelectDropdownProps<TFieldValues>) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>(initialSelectedOptions)
  const [isOpen, setIsOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [debouncedSearchInput] = useDebounce(searchInput, 400)

  useEffect(() => {
    onSearchInputChange?.(debouncedSearchInput)
  }, [debouncedSearchInput, onSearchInputChange])

  const trimmedSearchInput = searchInput.trim()
  const isLocalSearch = creatable && !onSearchInputChange

  const availableOptions = useMemo<MultiSelectOption[]>(() => {
    const knownOptionIds = new Set(options.map((option) => option.id))
    const createdOptions = selectedOptions.filter((id) => !knownOptionIds.has(id)).map((id) => ({ id, label: id }))
    return [...createdOptions, ...options]
  }, [options, selectedOptions])

  const visibleOptions = useMemo(
    () =>
      isLocalSearch && trimmedSearchInput
        ? availableOptions.filter((option) => option.label.toLowerCase().includes(trimmedSearchInput.toLowerCase()))
        : availableOptions,
    [availableOptions, isLocalSearch, trimmedSearchInput],
  )

  const canCreateSearchInput =
    creatable && !!trimmedSearchInput && !availableOptions.some((option) => option.label.toLowerCase() === trimmedSearchInput.toLowerCase())

  const handleCreateOption = () => {
    if (!creatable || !trimmedSearchInput) {
      return
    }
    const existingOption = availableOptions.find((option) => option.label.toLowerCase() === trimmedSearchInput.toLowerCase())
    const optionId = existingOption?.id ?? trimmedSearchInput
    setSelectedOptions((previousSelectedOptions) =>
      previousSelectedOptions.includes(optionId) ? previousSelectedOptions : [...previousSelectedOptions, optionId],
    )
    setSearchInput('')
  }

  // Sync the draft selection when the prop content changes externally.
  useDeepCompareEffect(() => {
    setSelectedOptions(initialSelectedOptions)
  }, [initialSelectedOptions])

  const latestOnSelectedChanged = useLatest(onSelectedChanged)
  const latestInitialSelectedOptions = useLatest(initialSelectedOptions)

  useEffect(() => {
    if ((!changeOnClose || !isOpen) && !sameSelection(selectedOptions, latestInitialSelectedOptions.current)) {
      latestOnSelectedChanged.current?.(selectedOptions)
    }
  }, [selectedOptions, isOpen, changeOnClose, latestOnSelectedChanged, latestInitialSelectedOptions])

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen)
    }
  }

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const optionId = event.target.value
    const isChecked = event.target.checked
    const newSelectedOptions = isChecked ? [...selectedOptions, optionId] : selectedOptions.filter((selectedOptionId) => selectedOptionId !== optionId)
    setSelectedOptions(newSelectedOptions)
  }
  const containerRef = useRef<HTMLDivElement>(null)

  useOutsideClick(containerRef, () => setIsOpen(false))
  const { onChange = undefined, onBlur = undefined, name: regName = undefined, ref = undefined } = register ? { ...register(name) } : {}

  const selectableOptionIds = visibleOptions.filter((o) => !o.disabled).map((o) => o.id)

  const selectedLabels = selectedOptions.map((id) => availableOptions.find((option) => option.id === id)?.label ?? id)
  const joinedSelectedLabels = selectedLabels.join(', ')

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedOptions([...selectedOptions, ...selectableOptionIds.filter((id) => !selectedOptions.includes(id))])
    } else {
      setSelectedOptions(selectedOptions.filter((id) => !selectableOptionIds.includes(id)))
    }
  }
  return (
    <Container ref={containerRef} className={className}>
      <Row>
        <Col className='p-0 w-100 position-relative'>
          <div
            className={classNames(`${styles.multiSelectContainer}`, containerClassName, {
              [styles.multiSelectContainerOpen]: isOpen,
              [styles.multiSelectContainerInvalid]: isInvalid,
              [styles.multiSelectContainerDisabled]: disabled,
            })}
            role='button'
            id='multiSelectDropdown'
            onClick={toggleDropdown}
          >
            {TitleComponent ? (
              TitleComponent
            ) : selectedOptions.length ? (
              <div className='d-flex gap-1 flex-wrap mw-0'>
                <div className='paragraph-medium text-truncate' title={showSelectedLabels ? joinedSelectedLabels : undefined}>
                  {showSelectedLabels
                    ? joinedSelectedLabels
                    : selectedOptions.length === 1
                      ? (selectedLabels[0] ?? '1 option selected')
                      : `${selectedOptions.length} options selected`}
                </div>
              </div>
            ) : (
              <div className='paragraph-medium zocks-text-gray-500'>{placeholder}</div>
            )}
            <div className='d-flex align-items-center zocks-gap-0.5'>
              {clearable && selectedOptions.length ? (
                <i
                  className='bi bi-x'
                  role='button'
                  aria-label='Clear selection'
                  onClick={(event) => {
                    event.stopPropagation()
                    setSelectedOptions([])
                  }}
                />
              ) : null}
              {isOpen ? <i className='bi bi-chevron-contract'></i> : <i className='bi bi-chevron-expand'></i>}
            </div>
          </div>

          <div className={`overflow-auto dropdown-menu ${isOpen ? 'show' : ''} ${styles.multiSelectDropdownMenu}`} aria-labelledby='multiSelectDropdown'>
            {onSearchInputChange || creatable ? (
              <Form.Control
                type='text'
                value={searchInput}
                onChange={(event) => setSearchInput(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' || !creatable) {
                    return
                  }
                  event.preventDefault()
                  handleCreateOption()
                }}
                placeholder={creatable ? 'Search or press Enter to add' : 'Search'}
                aria-label='Search options'
                className='mb-1'
              />
            ) : null}
            {canCreateSearchInput ? (
              <button
                type='button'
                className='dropdown-item px-2 paragraph-medium text-truncate'
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleCreateOption}
              >
                <i className='bi bi-plus-lg me-1' />
                Add &quot;{trimmedSearchInput}&quot;
              </button>
            ) : null}
            <div className={`${styles.multiSelectDropdownOptionsContainer}`}>
              {loading || (!!onSearchInputChange && searchInput !== debouncedSearchInput) ? (
                <span className='d-flex py-3 align-items-center justify-content-center'>
                  <Spinner size='sm' />
                </span>
              ) : visibleOptions.length ? (
                <>
                  {showSelectAllOption && (
                    <fieldset key='all'>
                      <Form.Check
                        name='All'
                        type='checkbox'
                        id={`${regName || name}_option_all`}
                        label='All'
                        checked={selectableOptionIds.length > 0 && selectableOptionIds.every((id) => selectedOptions.includes(id))}
                        onChange={handleSelectAllChange}
                        onBlur={onBlur}
                      />
                      <Dropdown.Divider className='my-1' />
                    </fieldset>
                  )}
                  {visibleOptions.map((option) => (
                    <fieldset key={option.id} disabled={option.disabled}>
                      <Form.Check
                        name={regName || name}
                        type='checkbox'
                        id={`${regName || name}_option_${option.id}`}
                        label={option.label}
                        checked={selectedOptions.includes(option.id)}
                        onChange={onChange || handleOptionChange}
                        value={option.id}
                        onBlur={onBlur}
                        ref={ref}
                      />
                    </fieldset>
                  ))}
                </>
              ) : (
                <span className='d-flex flex-column align-items-center justify-content-center text-center py-3 gap-2'>
                  <i className='bi bi-search fs-2 lh-1 zocks-text-gray-500' />
                  <span className='paragraph-small mt-2 zocks-text-gray-900'>
                    {trimmedSearchInput ? `No results for “${trimmedSearchInput}”` : 'No options'}
                  </span>
                </span>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
